import React, { useEffect, useState } from 'react';
import { Card, Col, Divider, Grid, Row, Table, Typography } from 'antd';
import MainLogo from '@app/assets/images/logo.jpg';
import SecondLogo from '@app/assets/images/logo_crop.png';
import { getInvoice } from '@app/api/prImportFreeSales.api';
import { getLabInvoice, createLabTask } from '@app/api/labs.api';

import type { ColumnsType } from 'antd/es/table';

interface InvoiceProps {
  id: any;
  setCurrentBreadcrumName: React.Dispatch<React.SetStateAction<string>>;
  setIsPayable: React.Dispatch<React.SetStateAction<boolean>>;
}

const { useBreakpoint } = Grid;

const Invoice: React.FC<InvoiceProps> = ({ id, setCurrentBreadcrumName, setIsPayable }) => {
  const screens = useBreakpoint();
  const isMobile = screens.xs;

  const [invoice, setInvoice] = useState<any>(null);
  const [isLab, setIsLab] = useState<boolean>(false);
  const [divisionCodeToName, setDivisionCodeToName] = useState<{ [code: string]: string }>({});

  useEffect(() => {
    const storedIsLab = localStorage.getItem('isLab');
    setIsLab(storedIsLab === 'true');
  }, []);

  useEffect(() => {
    if (id == null) return;

    const fetchInvoice = async () => {
      try {
        let data;
        if (isLab) {
          data = await getLabInvoice(id);
        } else {
          data = await getInvoice(id);
        }
        setInvoice(data.invoice);
        setCurrentBreadcrumName(`Invoice #${data.invoice.invoice_number}`);
        setIsPayable(data.invoice.is_payable);
      } catch (error) {
        console.error('Failed to fetch invoice:', error);
      }
    };

    fetchInvoice();
  }, [id, isLab]);

  useEffect(() => {
    if (!isLab) return;

    const fetchDivisionMapping = async () => {
      try {
        const data = await createLabTask('REGULAR_TEST');
        const mapping: { [code: string]: string } = {};

        data.lab_division_options.forEach((option: any) => {
          const match = option.label.match(/^([^-]+)/);
          if (match) {
            const code = match[1];
            mapping[code] = option.label;
          }
        });

        setDivisionCodeToName(mapping);
      } catch (error) {
        console.error('Failed to fetch division options:', error);
      }
    };

    fetchDivisionMapping();
  }, [isLab]);

  if (!invoice) return null;

  const invoiceItemsGroupedByDivision = () => {
    if (!isLab) return [];

    const grouped: { [divisionCode: string]: any[] } = {};

    invoice.invoice_lines.forEach((line: any, index: number) => {
      const match = line.description?.match(/^([^/]+)/);
      const divisionCode = match ? match[1] : 'Unknown Division';
      //console.log(`Line ${index} division code:`, divisionCode);
      if (!grouped[divisionCode]) grouped[divisionCode] = [];
      grouped[divisionCode].push({
        key: `${divisionCode}-${index}`,
        itemCode: line.itemable?.code || '-',
        description: line.itemable?.name || '-',
        qty: line.qty || '-',
        price: `${line.unit_price}`,
        total: `${line.total_price}`,
      });
    });

    const dataSource: any[] = [];

    Object.entries(grouped).forEach(([divisionCode, lines]) => {
      const friendlyName = divisionCodeToName[divisionCode] || divisionCode;

      dataSource.push({
        key: `header-${divisionCode}`,
        itemCode: friendlyName,
        divisionCode,
        isHeader: true,
      });
      dataSource.push(...lines);
    });

    return dataSource;
  };

  const invoiceItems = invoice.invoice_lines.map((line: any, index: number) => ({
    key: index.toString(),
    itemCode: line.itemable?.code || '-',
    description: line.description || '-',
    qty: line.qty || '-',
    price: `${line.unit_price}`,
    total: `${line.total_price}`,
  }));

  const columns: ColumnsType<any> = [
    {
      title: 'Item Code',
      dataIndex: 'itemCode',
      key: 'itemCode',
      render: (text: any, record: any) => {
        if (record.isHeader) {
          return {
            children: <strong>{divisionCodeToName[record.divisionCode] || record.divisionCode}</strong>,
            props: { colSpan: 5, style: { background: '#f0f2f5' } },
          };
        }
        if (record.isTotal) return { children: null, props: { colSpan: 1 } };
        return text;
      },
    },
    {
      title: 'Item Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: any, record: any) =>
        record.isHeader || record.isTotal ? { children: null, props: { colSpan: 0 } } : text,
    },
    {
      title: 'QTY',
      dataIndex: 'qty',
      key: 'qty',
      render: (text: any, record: any) =>
        record.isHeader || record.isTotal ? { children: null, props: { colSpan: 0 } } : text,
    },
    {
      title: 'Price (LKR)',
      dataIndex: 'price',
      key: 'price',
      align: 'right',
      render: (text: any, record: any) => {
        if (record.isTotal) return { children: <strong>{text}</strong>, props: {} };
        return record.isHeader ? { children: null, props: { colSpan: 0 } } : text;
      },
    },
    {
      title: 'Total (LKR)',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: (text: any, record: any) => {
        if (record.isTotal) return { children: <strong>{text}</strong>, props: {} };
        return record.isHeader ? { children: null, props: { colSpan: 0 } } : text;
      },
    },
  ];

  const formatDate = (date: string) => {
    const newDate = new Date(date);
    return newDate.toLocaleString('sv-SE');
  };

  return (
    <div id="invoice-content">
      <Card
        style={{
          borderRadius: '16px',
          padding: '20px',
          textAlign: 'center',
          margin: 'auto',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <img src={MainLogo} alt="Main Logo" style={{ height: isMobile ? '60px' : '80px' }} />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: isMobile ? 'center' : 'space-between',
            alignItems: 'center',
            marginTop: '30px',
          }}
        >
          <div style={{ flex: isMobile ? 'none' : '1', textAlign: isMobile ? 'center' : 'left' }}>
            <img src={SecondLogo} alt="Second Logo" style={{ height: isMobile ? '50px' : '70px' }} />
          </div>
          <div style={{ flex: '2', textAlign: 'center' }}>
            <h2 style={{ marginTop: '30px', fontSize: isMobile ? '24px' : '40px', fontWeight: 'bold' }}>Invoice</h2>
          </div>

          {/* Invisible Spacer in Desktop Mode for Proper Centering */}
          {!isMobile && <div style={{ flex: '1' }} />}
        </div>

        <Divider style={{ backgroundColor: '#D3D3D3', marginTop: '10px' }} />

        <div style={{ paddingLeft: isMobile ? 0 : '80px', marginTop: isMobile ? '40px' : '60px' }}>
          <Row
            gutter={[64, 48]}
            style={{
              marginTop: '30px',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
            }}
          >
            <Col
              xs={24}
              md={11}
              style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start' }}
            >
              <Typography.Text strong>Invoice Number:</Typography.Text>
              <div>{invoice?.invoice_number}</div>
            </Col>

            <Col xs={24} md={11} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography.Text strong>Address:</Typography.Text>
              <div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>
                {invoice?.department?.name} ,
                <br />
                {invoice?.department?.address}
              </div>
            </Col>

            <Col
              xs={24}
              md={11}
              style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start' }}
            >
              <Typography.Text strong>Date:</Typography.Text>
              <div>{formatDate(invoice.updated_at)}</div>
            </Col>

            {!isLab && (
              <Col xs={24} md={11} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography.Text strong>Department:</Typography.Text>
                <div>{invoice.department.name}</div>
              </Col>
            )}
          </Row>
        </div>

        <Divider style={{ backgroundColor: '#D3D3D3', marginTop: isMobile ? '40px' : '60px' }} />
        <h2 style={{ marginTop: '30px', fontSize: isMobile ? '16px' : '26px', fontWeight: 'bold', color: '#A9A9A9' }}>
          {invoice.partner_type === 'App\\Models\\Customer' ? 'Customer Details' : 'Department Details'}
        </h2>
        <div style={{ paddingLeft: isMobile ? 0 : '80px', marginTop: isMobile ? '40px' : '60px' }}>
          <Row
            gutter={[64, 64]}
            style={{
              marginTop: '30px',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
            }}
          >
            <Col
              xs={24}
              md={11}
              style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start' }}
            >
              <Typography.Text strong>Name:</Typography.Text>
              <div>{invoice.partner?.name || 'N/A'}</div> {/* customer name */}
            </Col>

            <Col xs={24} md={11} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography.Text strong>Address:</Typography.Text>
              <div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>
                {invoice.partner?.address || 'N/A'}
              </div>{' '}
              {/* customer address */}
            </Col>

            {invoice.partner_type === 'App\\Models\\Customer' && (
              <>
                <Col
                  xs={24}
                  md={11}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isMobile ? 'center' : 'flex-start',
                  }}
                >
                  <Typography.Text strong>Email:</Typography.Text>
                  <div>{invoice.partner?.user?.email}</div>
                </Col>

                <Col
                  xs={24}
                  md={11}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Typography.Text strong>Contact No:</Typography.Text>
                  <div>{invoice.partner?.contact_no}</div>
                </Col>
              </>
            )}
          </Row>
        </div>

        <Divider
          style={{
            backgroundColor: '#D3D3D3',
            marginTop: isMobile ? '40px' : '60px',
            marginBottom: isMobile ? '40px' : '60px',
          }}
        />
        <Table
          columns={columns}
          dataSource={isLab ? invoiceItemsGroupedByDivision() : invoiceItems}
          pagination={false}
          bordered
          style={{ marginTop: '30px' }}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={4}>
                <span style={{ textAlign: 'left', fontWeight: 'bold', display: 'block' }}>Total:</span>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <span style={{ fontWeight: 'bold' }}>{invoice.total}</span> {/* total */}
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
          scroll={{ x: true }} // Ensures table scrolls on small screens
        />
        {invoice?.status == 'VALIDATED' && (
          <p style={{ textAlign: 'left', marginTop: '30px' }}>
            {' '}
            * <span style={{ marginRight: '10px' }}></span> No payment required. Validated as Zero Invoicing.
          </p>
        )}
        <Divider style={{ backgroundColor: '#D3D3D3', marginTop: isMobile ? '40px' : '40px' }} />
        <div style={{ paddingLeft: isMobile ? 0 : '80px', marginTop: isMobile ? '20px' : '30px' }}>
          <Row
            gutter={[64, 64]}
            style={{
              marginTop: '30px',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
            }}
          >
            <Col
              xs={24}
              md={24}
              lg={8}
              style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start' }}
            >
              <Typography.Text strong>Bank:</Typography.Text>
              <div> People&apos;s Bank</div>
            </Col>
            <Col
              xs={24}
              md={24}
              lg={8}
              style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start' }}
            >
              <Typography.Text strong>Account:</Typography.Text>
              <div>0000 0000 000000</div>
            </Col>
            <Col
              xs={24}
              md={24}
              lg={8}
              style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start' }}
            >
              <Typography.Text strong>Branch:</Typography.Text>
              <div>Katunayake</div>
            </Col>
          </Row>
        </div>
        <p style={{ textAlign: 'left', marginTop: '100px' }}>
          {' '}
          * <span style={{ marginRight: '10px' }}></span> This is a computer generated Invoice
        </p>
      </Card>
    </div>
  );
};

export default Invoice;
